import { useEffect, useState } from "react";
import { api, frDate } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Link2, CheckCircle2, XCircle } from "lucide-react";

const empty = {
  first_name: "", last_name: "", email: "", phone: "", address: "",
  birth_date: "", skills: "", availability: "",
  join_date: new Date().toISOString().slice(0, 10),
  total_hours: 0, status: "actif", notes: "",
};

export default function Benevoles() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/volunteers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, total_hours: Number(form.total_hours) || 0 };
      if (edit) await api.put(`/volunteers/${edit.id}`, payload);
      else await api.post("/volunteers", payload);
      toast.success(edit ? "Bénévole modifié." : "Bénévole ajouté.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer ${b.first_name} ${b.last_name} ?`)) return;
    try {
      await api.delete(`/volunteers/${b.id}`);
      toast.success("Bénévole supprimé.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const reject = async (b) => {
    if (!window.confirm(`Rejeter la candidature de ${b.first_name} ${b.last_name} ?`)) return;
    try {
      await api.delete(`/volunteers/${b.id}`);
      toast.success(`Candidature de ${b.first_name} ${b.last_name} rejetée.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const validate = async (b) => {
    try {
      await api.post(`/volunteers/${b.id}/validate`);
      toast.success(`${b.first_name} ${b.last_name} validé(e).`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/inscription-benevole`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Lien d'inscription bénévole copié."),
      () => toast.error("Copie impossible — copiez manuellement : " + url)
    );
  };

  return (
    <div data-testid="benevoles-page">
      <PageHeader
        eyebrow="Engagement citoyen"
        title="Bénévoles"
        description="Gérez les bénévoles de l'association, leurs compétences et heures d'engagement."
        actions={
          <div className="flex gap-2">
            <Button data-testid="copy-public-volunteer-link-btn" variant="outline" onClick={copyPublicLink}>
              <Link2 size={16} className="mr-1" /> Lien d'inscription
            </Button>
            <Button data-testid="new-volunteer-btn" onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
              <Plus size={16} className="mr-1" /> Nouveau bénévole
            </Button>
          </div>
        }
      />

      <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
        {rows.some((b) => b.pending_validation) && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2 text-sm">
            <span className="font-medium">{rows.filter((b) => b.pending_validation).length}</span>
            {" "}candidature(s) bénévole en attente de validation.
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 small-caps">
            <tr>
              <th className="text-left py-3 px-4">Bénévole</th>
              <th className="text-left py-3 px-4">Contact</th>
              <th className="text-left py-3 px-4">Compétences</th>
              <th className="text-left py-3 px-4">Heures</th>
              <th className="text-left py-3 px-4">Statut</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400">Aucun bénévole pour l'instant.</td></tr>}
            {rows.map((b) => (
              <tr key={b.id} className="border-t border-brand-border hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    {b.first_name} {b.last_name}
                    {b.pending_validation && (
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] px-1.5 py-0">EN ATTENTE</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">Depuis {frDate(b.join_date)}</div>
                </td>
                <td className="py-3 px-4 text-gray-700">
                  <div>{b.email || "-"}</div>
                  <div className="text-xs text-gray-400">{b.phone}</div>
                </td>
                <td className="py-3 px-4 text-gray-600 text-sm max-w-xs truncate">{b.skills || "-"}</td>
                <td className="py-3 px-4"><Badge className="bg-brand-green text-white">{b.total_hours} h</Badge></td>
                <td className="py-3 px-4"><Badge variant="outline" className="capitalize">{b.status}</Badge></td>
                <td className="py-3 px-4 text-right space-x-1">
                  {b.pending_validation && (
                    <>
                      <Button data-testid={`validate-vol-${b.id}`} size="sm" variant="outline" onClick={() => validate(b)} className="text-brand-green border-brand-green/30">
                        <CheckCircle2 size={14} className="mr-1" /> Valider
                      </Button>
                      <Button data-testid={`reject-vol-${b.id}`} size="sm" variant="outline" onClick={() => reject(b)} className="text-red-600 border-red-200">
                        <XCircle size={14} className="mr-1" /> Rejeter
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(b); setForm({ ...empty, ...b }); setOpen(true); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(b)}><Trash2 size={14} className="text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier" : "Nouveau bénévole"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div><Label>Prénom *</Label><Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} /></div>
            <div><Label>Nom *</Label><Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div><Label>Date de naissance</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({...form, birth_date: e.target.value})} /></div>
            <div><Label>Date d'entrée</Label><Input type="date" value={form.join_date || ""} onChange={(e) => setForm({...form, join_date: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Compétences</Label><Input value={form.skills} onChange={(e) => setForm({...form, skills: e.target.value})} placeholder="Ex: animation, communication, logistique..." /></div>
            <div><Label>Disponibilités</Label><Input value={form.availability} onChange={(e) => setForm({...form, availability: e.target.value})} placeholder="Week-ends, soirs..." /></div>
            <div><Label>Total heures</Label><Input type="number" value={form.total_hours} onChange={(e) => setForm({...form, total_hours: e.target.value})} /></div>
            <div>
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="inactif">Inactif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
